// AppFooter.jsx
import React from 'react'
import {
  Box, Container, Stack, Typography, 
  Link as MLink, Select, MenuItem, FormControl
} from '@mui/material'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import logo from '@assets/images/logo/logo_w.svg'
import { useIsMobile } from '@/hooks/Responsive'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import { visuallyHidden } from '@mui/utils'

// 현재 경로 + 타겟 언어 → 다음 경로 계산
function switchLangPath(pathnameWithSearch, targetLang) {
  const [pathname, search = ''] = pathnameWithSearch.split('?');
  const searchPart = search ? `?${search}` : '';

  if (targetLang === 'en') {
    if (pathname === '/') return `/en${searchPart}`;
    if (pathname.startsWith('/en')) return pathname + searchPart; // 이미 en
    return `/en${pathname}${searchPart}`; // /about → /en/about
  }

  // targetLang === 'ko'
  if (pathname === '/en') return `/${searchPart}`;
  if (pathname.startsWith('/en/')) {
    const nextPath = pathname.replace(/^\/en/, '') || '/';
    return `${nextPath}${searchPart}`;
  }

  return pathname + searchPart; // 이미 ko
}

export default function AppFooter() {
  const { isMobile } = useIsMobile()
  const lang = useLang()  // 'ko' | 'en' (URL 기반)
  const location = useLocation()
  const navigate = useNavigate()

  const handleLangChange = (value) => {
    const next = value === "ENG" ? "en" : "ko";

    const currentPathWithSearch = location.pathname + location.search;
    const nextPath = switchLangPath(currentPathWithSearch, next);

    // URL 기준을 맞추면서, 번역 스크립트용 localStorage도 같이 업데이트
    localStorage.setItem("lang", next);

    navigate(nextPath, { replace: true });
  };

  const familySites = [
    { label: "한국방송통신대학교", href: "https://www.knou.ac.kr" },
    { label: "교육부", href: "https://www.moe.go.kr" }
  ]

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'grey.900',
        color: 'grey.300',
        pt: 4.5,
        pb: 3.5
      }}
    >
      <Container maxWidth="xl">
        <Stack
          direction="column"
          justifyContent="space-between"
          alignItems={isMobile ? 'flex-start' : 'flex-start'}
        >
          <Stack
            direction={isMobile ? 'column' : 'row'}
            flexWrap="wrap"
            alignItems={isMobile ? 'flex-start' : 'center'}
            {...(isMobile && { spacing: 3 })}
            sx={{ width: '100%' }}
          >
            {/* 로고 */}
            <Box
              component="img"
              src={logo}
              alt="한국방송통신대학교 OpenVLab"
              sx={{ height: isMobile ? '34px' : '36px' }}
            />

            {/* 정책 */}
            <Stack
              direction="row"
              flexWrap="wrap"
              sx={{
                columnGap: isMobile ? 2 : 3,
                rowGap: 1,
                ml: 10,
                '& a': {
                  color: 'grey.100',
                  typography: 'body2',
                  '&:hover': { color: 'common.white' },
                  '&.privacy': { color: 'primary.main' }
                },
              }}
            >
              <MLink component={RouterLink} to={buildLangHref('/policy/terms', lang)}>이용약관</MLink>
              <MLink component={RouterLink} to={buildLangHref('/policy/privacy', lang)} className="privacy">개인정보처리방침</MLink>
              <MLink component={RouterLink} to={buildLangHref('/policy/copyright', lang)}>저작권보호정책</MLink>
              <MLink component={RouterLink} to={buildLangHref('/alarm/inquiry', lang)}>문의 및 제안</MLink>
            </Stack>

            {/* 언어 + 패밀리 */}
            <Stack direction="row" sx={{ width: {xs: '100%', lgxl: 'auto'}, mt: {md: 1, xl: 0}, ml: 'auto', gap: 1 }}>
              <FormControl sx={{ width: isMobile ? 'auto' : '160px', flex: isMobile ? 1 : 'auto' }}>
                <Typography
                  component="label"
                  sx={visuallyHidden}
                  htmlFor="footer-lang-label"
                >
                  언어 선택
                </Typography>
                <Select
                  value={lang === "en" ? "ENG" : "KOR"}
                  onChange={(e) => handleLangChange(e.target.value)}
                  IconComponent={KeyboardArrowDownRoundedIcon}
                  MenuProps={{
                    anchorOrigin: { vertical: 'top', horizontal: 'left' },
                    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
                    PaperProps: {
                      sx: {
                        boxShadow: 'none',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                    },
                  }}
                  inputProps={{
                    'id': 'footer-lang-label',
                    'labelId': 'footer-lang-label',
                  }}
                  sx={{
                    borderRadius: 2,
                    height: isMobile ? '40px' : '40px',
                    lineHeight: isMobile ? '40px' : '40px',
                    '&:has(.MuiSelect-select[aria-expanded="true"])': {
                      borderRadius: 2,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                    },
                  }}
                >
                  <MenuItem value="KOR">KOR</MenuItem>
                  <MenuItem value="ENG">ENG</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ width: isMobile ? 'auto' : '240px', flex: isMobile ? 2 : 'auto' }}>
                <Typography
                  component="label"
                  sx={visuallyHidden}
                  htmlFor="footer-family-label"
                >
                  Family Site 선택
                </Typography>
                <Select
                  value=""
                  displayEmpty
                  IconComponent={KeyboardArrowDownRoundedIcon}
                  MenuProps={{
                    anchorOrigin: { vertical: 'top', horizontal: 'left' },
                    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
                    PaperProps: {
                      sx: {
                        boxShadow: 'none',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                    },
                  }}
                  renderValue={() => "Family Site"}
                  inputProps={{
                    'id': 'footer-family-label',
                    'labelId': 'footer-family-label',
                    'aria-label': 'Family Site 선택',
                  }}
                  sx={{
                    borderRadius: 2,
                    height: isMobile ? '40px' : '40px',
                    lineHeight: isMobile ? '40px' : '40px',
                    '&:has(.MuiSelect-select[aria-expanded="true"])': {
                      borderRadius: 2,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                    },
                  }}
                >
                  {familySites.map((item) => (
                    <MenuItem
                      key={item.label}
                      component="a"
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          {/* 주소 */}
          <Typography variant="body2" color="grey.light" sx={{ mt: isMobile ? 2 : 3 }}>
            (03087) 서울특별시 종로구 대학로 86(동숭동), 오픈브이랩
            <br />
            Copyright © Korea National Open University. All Rights Reserved.
          </Typography>
        </Stack>
      </Container>
    </Box>
  )
}